/**
 * Form-state helpers for the datum temperature / Q-value workbench
 * (ASTM C1074-19 Appendix X1).
 *
 * The fitting itself runs on the backend (services/datum_q_calc.py). This
 * module only turns the editable grid into an API request with clear
 * validation messages, and evaluates the returned curve/line parameters so
 * the charts can draw them.
 */
import type { DatumQAnalyzeRequest, DatumQBathInput } from '../../services/maturity';

export interface RowForm {
  id: string;
  age: string;      // days
  strength: string; // in the chosen unit
}

export interface BathForm {
  id: string;
  tempC: string;
  rows: RowForm[];
}

export interface BuildResult {
  request: DatumQAnalyzeRequest | null;
  errors: string[];
}

let seq = 0;
const uid = (prefix: string): string => `${prefix}${++seq}`;

export const DEFAULT_ROWS = 7; // ASTM example: 7 test ages per bath
export const MIN_BATHS = 3;
export const MIN_ROWS = 4;

export const newRow = (): RowForm => ({ id: uid('r'), age: '', strength: '' });

export const newBath = (tempC = '', rows = DEFAULT_ROWS): BathForm => ({
  id: uid('b'),
  tempC,
  rows: Array.from({ length: rows }, newRow),
});

/** Three empty baths, the minimum the standard asks for. */
export const emptyBaths = (): BathForm[] => [newBath(), newBath(), newBath()];

export const bathsFromExample = (baths: DatumQBathInput[]): BathForm[] =>
  baths.map((b) => ({
    id: uid('b'),
    tempC: String(b.bath_temp_c),
    rows: b.points.map((p) => ({ id: uid('r'), age: String(p.age_days), strength: String(p.strength) })),
  }));

const isBlank = (s: string): boolean => s.trim() === '';

const num = (s: string): number | null => {
  if (isBlank(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/**
 * Validate the grid and build the request. Rows with both cells blank are
 * ignored, so a half-filled grid is fine; a row with only one cell filled is
 * reported. Messages are plain language and name the bath by its position
 * (and temperature when known).
 */
export function buildRequest(baths: BathForm[], strengthUnit: string): BuildResult {
  const errors: string[] = [];
  const out: DatumQBathInput[] = [];

  if (baths.length < MIN_BATHS) {
    errors.push(`Enter at least ${MIN_BATHS} bath temperatures.`);
  }

  const seenTemps: number[] = [];
  baths.forEach((bath, i) => {
    const label = isBlank(bath.tempC) ? `Bath ${i + 1}` : `Bath ${i + 1} (${bath.tempC.trim()} °C)`;
    const temp = num(bath.tempC);
    if (temp === null) {
      errors.push(`${label}: enter the water bath temperature in °C.`);
    } else if (seenTemps.some((t) => Math.abs(t - temp) < 0.05)) {
      errors.push(`${label}: this temperature is already used by another bath.`);
    } else {
      seenTemps.push(temp);
    }

    const points: { age_days: number; strength: number }[] = [];
    bath.rows.forEach((row, r) => {
      const ageBlank = isBlank(row.age);
      const strBlank = isBlank(row.strength);
      if (ageBlank && strBlank) return;
      const age = num(row.age);
      const strength = num(row.strength);
      if (age === null || strength === null) {
        errors.push(`${label}, row ${r + 1}: enter both the age and the strength.`);
        return;
      }
      if (age <= 0) {
        errors.push(`${label}, row ${r + 1}: the age must be greater than 0 days.`);
        return;
      }
      if (strength < 0) {
        errors.push(`${label}, row ${r + 1}: the strength cannot be negative.`);
        return;
      }
      points.push({ age_days: age, strength });
    });

    if (points.length < MIN_ROWS) {
      errors.push(`${label}: enter at least ${MIN_ROWS} test ages (the ASTM example uses 7).`);
    }
    if (temp !== null) {
      out.push({ bath_temp_c: temp, points });
    }
  });

  if (errors.length > 0) return { request: null, errors };
  return { request: { baths: out, strength_unit: strengthUnit }, errors: [] };
}

/** Eq X1.1: S = Su k (t - t0) / (1 + k (t - t0)), zero before t0. */
export const hyperbolicStrength = (t: number, su: number, k: number, t0: number): number => {
  const u = k * (t - t0);
  return u <= 0 ? 0 : (su * u) / (1 + u);
};

/** Points along the fitted strength-age curve from t0 to maxAge. */
export function hyperbolicCurve(
  su: number,
  k: number,
  t0: number,
  maxAge: number,
  steps = 80,
): number[][] {
  const pts: number[][] = [];
  const start = Math.max(t0, 0);
  if (!(maxAge > start)) return pts;
  for (let i = 0; i <= steps; i++) {
    const t = start + ((maxAge - start) * i) / steps;
    pts.push([round(t, 3), round(hyperbolicStrength(t, su, k, t0), 3)]);
  }
  return pts;
}

/** Two end points of y = slope * x + intercept between x0 and x1. */
export const linePoints = (slope: number, intercept: number, x0: number, x1: number): number[][] => [
  [x0, slope * x0 + intercept],
  [x1, slope * x1 + intercept],
];

export const round = (n: number, dp: number): number => {
  const f = Math.pow(10, dp);
  return Math.round(n * f) / f;
};

export const fmt = (n: number | null | undefined, dp: number): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '-' : n.toFixed(dp);

export const cToFNumber = (c: number): number => c * 1.8 + 32;
